import {
  AdviceCoverageName,
  AdvicePriceCalculationResult,
  Price,
} from "Store/akaAdvice/interfaces/AkaAdviceState";
import {
  AkaCriticalIllness,
  AkaGroupLifeCompositions,
  AkaGroupLifeCoverage,
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCompositions,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";

import { Option } from "@storeblocks/select";
import { formatNumber } from "Formatting/index";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import {
  CoverageNeed,
  PaymentRestriction,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getAllNeedCoverages,
  isCoverageSelectedInCard,
} from "Store/akaAdvice/interfaces/getters";
import { sum } from "Utils/arrayUtils";
import { isDefined } from "Utils/functions";
import { AkaAdvicePriceFormValues } from "./interfaces";

export const getAkaGroupLifeGLevelOptions = (
  name: "akaGroupLifeDeathGLevel" | "incapacityOfWorkGLevel",
  validSteps: number[] | null,
  baseAmount: number,
): Option[] => {
  if (!isDefined(validSteps)) {
    return [];
  }
  return validSteps.map((validStep) => ({
    label: `${formatNumber(
      String(baseAmount * validStep),
      false,
    )} kr (${validStep}G)`,
    value: String(validStep),
    id: `${name}-${validStep}G`,
  }));
};

export const getAkaGLevelOptions = (
  name:
    | "timeLimitedDisabilityInterestGLevel"
    | "akaCriticalIllnessCoverageAmountGLevel",
  maxPayout: number,
  baseAmount: number,
): Option[] => {
  const steps = maxPayout;
  const options: Option[] = [];
  for (let index = 1; index <= steps; index++) {
    options.push({
      label: `${formatNumber(
        String(baseAmount * index),
        false,
      )} kr (${index}G)`,
      value: String(index),
      id: `${name}-${index}G`,
    });
  }
  return options;
};

export const getMonthlyPrice = (
  productCoverageName: ProductCoverageName,
  priceResults: AdvicePriceCalculationResult[],
): number => {
  switch (productCoverageName) {
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK],
        priceResults,
      );
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK],
        priceResults,
      );
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [
          AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
        ],
        priceResults,
      );
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [
          AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
        ],
        priceResults,
      );
    case ProductCoverageName.DISABILITY_FLEX_CAPITAL:
      return getCoverageMonthlyPrice(
        [DisabilityCoverage],
        [DisabilityCompositions.DISABILITY_CAPITAL_FLEX],
        priceResults,
      );
    case ProductCoverageName.DISABILITY_FLEX_PENSION:
      return getCoverageMonthlyPrice(
        [DisabilityCoverage],
        [
          DisabilityCompositions.DISABILITY_PENSION,
          DisabilityCompositions.DISABILITY_PENSION_3MONTHS,
        ],
        priceResults,
      );
    case ProductCoverageName.AKA_GROUP_LIFE_INSURED_DEATH:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_DEATH],
        priceResults,
      );
    case ProductCoverageName.AKA_GROUP_LIFE_CO_INSURED_DEATH:
      return getCoverageMonthlyPrice(
        [AkaGroupLifeCoverage],
        [AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_DEATH],
        priceResults,
      );
    case ProductCoverageName.DEATH:
      return getCoverageMonthlyPrice([DeathCoverage], [], priceResults);
    case ProductCoverageName.AKA_CRITICAL_ILLNESS:
      return getCoverageMonthlyPrice(
        [AkaCriticalIllness],
        [AkaCriticalIllness.AKA_CRITICAL_ILLNESS],
        priceResults,
      );
    case ProductCoverageName.AKA_CRITICAL_ILLNESS_CO_INSURED:
      return getCoverageMonthlyPrice(
        [AkaCriticalIllness],
        [AkaCriticalIllness.AKA_CRITICAL_ILLNESS_CO_INSURED],
        priceResults,
      );
    case ProductCoverageName.CRITICAL_ILLNESS:
      return getCoverageMonthlyPrice(
        [CriticalIllnessCoverage],
        [],
        priceResults,
      );
    default:
      return 0;
  }
};

type Compositions =
  | AkaGroupLifeCompositions[]
  | DisabilityCompositions[]
  | AkaCriticalIllness[];

export const getCoveragePrices = (
  coverage: Record<string, unknown>[],
  composites: Compositions,
  priceResults: AdvicePriceCalculationResult[],
): Price[] =>
  priceResults
    .filter((priceResult) => configIsInCoverages(coverage, priceResult))
    .map((priceResult) => mapPriceOrComposites(composites, priceResult))
    .flat();

const getCoverageMonthlyPrice = (
  coverage: Record<string, unknown>[],
  composites: Compositions,
  priceResults: AdvicePriceCalculationResult[],
): number =>
  getCoveragePrices(coverage, composites, priceResults)
    .map((price) => price.monthlyPricePrecise)
    .reduce(sum, 0);

const shouldUseComposites = (configName: AdviceCoverageName): boolean =>
  (Object.values(AkaGroupLifeCoverage) as string[]).includes(configName) ||
  (Object.values(AkaCriticalIllness) as string[]).includes(configName) ||
  (Object.values(DisabilityCoverage) as string[]).includes(configName);

const configIsInCoverages = (
  coverage: Record<string, unknown>[],
  price: AdvicePriceCalculationResult,
): boolean =>
  coverage.map(Object.values).flat().includes(price.configurationName);

const mapPriceOrComposites = (
  compositions: Compositions,
  priceResult: AdvicePriceCalculationResult,
): Price[] => {
  if (!shouldUseComposites(priceResult.configurationName)) {
    return priceResult.prices;
  }
  return Object.entries(priceResult.composites)
    .filter((composite) =>
      compositions
        .map((composition) => composition.toString())
        .includes(composite[0]),
    )
    .map((composite) => {
      if (isAkaMemberOnlyComposite(composite[0])) {
        return mapMemberOnly(composite[1].prices);
      } else if (isAkaCoInsuredOnlyComposite(composite[0])) {
        return mapCoInsuredOnly(composite[1].prices);
      }
      return composite[1].prices;
    })
    .flat();
};

const isAkaMemberOnlyComposite = (composite: string): boolean =>
  [
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_DEATH,
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
    AkaCriticalIllness.AKA_CRITICAL_ILLNESS,
  ].includes(composite as AkaGroupLifeCompositions | AkaCriticalIllness);

const isAkaCoInsuredOnlyComposite = (composite: string): boolean =>
  [
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_DEATH,
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK,
    AkaGroupLifeCompositions.AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
    AkaCriticalIllness.AKA_CRITICAL_ILLNESS_CO_INSURED,
  ].includes(composite as AkaGroupLifeCompositions | AkaCriticalIllness);

const mapMemberOnly = (prices: Price[]): Price[] =>
  prices.map((price) => ({
    ...price,
    memberMonthlyPrice: price.monthlyPrice,
    memberMonthlyPricePrecise: price.monthlyPricePrecise,
    memberYearlyPrice: price.yearlyPrice,
    memberYearlyPricePrecise: price.yearlyPricePrecise,
  }));

const mapCoInsuredOnly = (prices: Price[]): Price[] =>
  prices.map((price) => ({
    ...price,
    coInsuredMonthlyPrice: price.monthlyPrice,
    coInsuredMonthlyPricePrecise: price.monthlyPricePrecise,
    coInsuredYearlyPrice: price.yearlyPrice,
    coInsuredYearlyPricePrecise: price.yearlyPricePrecise,
  }));

export const getIndividualAdvicePlotRange = (
  isAdvisor: boolean,
  debtOrIncome: number,
  paymentRestriction: PaymentRestriction,
): number[] | undefined => {
  if (isAdvisor) {
    return undefined;
  }
  const maxPayout =
    paymentRestriction.maxPayouts[UserAuthorizationLevel.CUSTOMER];
  return debtOrIncome > maxPayout ? [0, debtOrIncome] : [0, maxPayout];
};

export const getCoveragesSelectedInCard = (
  need: CoverageNeed,
  formValues: Partial<AkaAdvicePriceFormValues>,
  isAdvisor: boolean,
): ProductCoverageName[] =>
  getAllNeedCoverages(need).filter((productCoverageName) =>
    isCoverageSelectedInCard(productCoverageName, formValues, isAdvisor),
  );
